import { useState, useEffect, useCallback } from 'react';
import { apiRequest } from '../config/api';
import { useToast } from '../context/ToastContext';

export default function useFormTemplates() {
  const [templates, setTemplates] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const { showSuccess, showError } = useToast();

  const fetchTemplates = useCallback(async (filters = {}) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.category) params.set('category', filters.category);
      if (filters.search) params.set('search', filters.search);
      if (filters.scope) params.set('scope', filters.scope);
      const qs = params.toString();
      const response = await apiRequest(`/form-templates/${qs ? `?${qs}` : ''}`);
      if (response.ok) {
        const data = await response.json();
        setTemplates(Array.isArray(data) ? data : []);
      }
    } catch (error) {
      console.error('Error fetching templates:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchCategories = useCallback(async () => {
    try {
      const response = await apiRequest('/form-templates/categories');
      if (response.ok) {
        const data = await response.json();
        setCategories(Array.isArray(data) ? data : []);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  }, []);

  const applyTemplate = useCallback(async (templateId, title) => {
    try {
      const params = new URLSearchParams();
      if (title) params.set('title', title);
      const qs = params.toString();
      const response = await apiRequest(`/form-templates/${templateId}/apply${qs ? `?${qs}` : ''}`, {
        method: 'POST',
      });
      if (response.ok) {
        const data = await response.json();
        showSuccess('Form created from template!');
        return data;
      } else {
        const err = await response.json();
        showError(err.detail || 'Failed to apply template');
        return null;
      }
    } catch (error) {
      console.error('Error applying template:', error);
      showError('Failed to create form from template');
      return null;
    }
  }, [showSuccess, showError]);

  const saveAsTemplate = useCallback(async ({ name, description, category, fields, settings }) => {
    try {
      const response = await apiRequest('/form-templates/', {
        method: 'POST',
        body: JSON.stringify({ name, description, category, fields, settings }),
      });
      if (response.ok) {
        const data = await response.json();
        showSuccess('Template saved!');
        return data;
      } else {
        const err = await response.json();
        showError(err.detail || 'Failed to save template');
        return null;
      }
    } catch (error) {
      console.error('Error saving template:', error);
      showError('Failed to save template');
      return null;
    }
  }, [showSuccess, showError]);

  const deleteTemplate = useCallback(async (templateId) => {
    try {
      const response = await apiRequest(`/form-templates/${templateId}`, {
        method: 'DELETE',
      });
      if (response.ok) {
        setTemplates(prev => prev.filter(t => t.id !== templateId));
        showSuccess('Template deleted');
        return true;
      } else {
        const err = await response.json();
        showError(err.detail || 'Failed to delete template');
        return false;
      }
    } catch (error) {
      console.error('Error deleting template:', error);
      showError('Failed to delete template');
      return false;
    }
  }, [showSuccess, showError]);

  // Initial fetch
  useEffect(() => {
    fetchTemplates();
    fetchCategories();
  }, [fetchTemplates, fetchCategories]);

  return {
    templates,
    categories,
    loading,
    fetchTemplates,
    fetchCategories,
    applyTemplate,
    saveAsTemplate,
    deleteTemplate,
  };
}
